import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ShieldCheck, FloppyDisk } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ROLES = [
  { key: 'deposit_handler', label: 'Deposit Handler' },
  { key: 'withdrawal_handler', label: 'Withdrawal Handler' },
  { key: 'support_handler', label: 'Support Handler' },
  { key: 'inquiry_handler', label: 'Inquiry Handler' },
  { key: 'calling_handler', label: 'Calling Handler' }
];

const PERMISSIONS_LIST = [
  { key: 'can_delete_chat', label: 'Allow Deleting Chat Rooms' },
  { key: 'can_block_clients', label: 'Allow Blocking/Unblocking Clients' },
  { key: 'can_send_broadcast', label: 'Allow Creating Broadcast Messages' },
  { key: 'can_manage_groups', label: 'Allow Group Chat Management' },
  { key: 'can_export_reports', label: 'Allow PDF/Excel Report Export' }
];

export default function Permissions() {
  const [selectedRole, setSelectedRole] = useState('deposit_handler');
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPermissions();
  }, [selectedRole]);

  const fetchPermissions = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/settings/permissions?role=${selectedRole}`);
      setPermissions(response.data || {});
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (key) => {
    setPermissions(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleSave = async () => {
    try {
      await axios.post(`${API}/settings/permissions`, {
        role: selectedRole,
        permissions
      });
      toast.success('Permissions saved successfully!');
    } catch (err) {
      toast.error('Failed to save permissions');
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-sm">
      <div className="flex items-center gap-3 mb-6 border-b pb-3">
        <ShieldCheck size={28} className="text-green-500" />
        <div>
          <h2 className="text-xl font-bold text-gray-800">Permissions Hub</h2>
          <p className="text-sm text-gray-500">Configure role-based access control and operator limits</p>
        </div>
      </div>

      <div className="mb-6">
        <label className="block text-sm font-semibold text-gray-700 mb-2">Select Handler Role</label>
        <select
          value={selectedRole}
          onChange={(e) => setSelectedRole(e.target.value)}
          className="w-full md:w-64 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
        >
          {ROLES.map(role => (
            <option key={role.key} value={role.key}>{role.label}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <p className="text-gray-500">Loading permissions...</p>
      ) : (
        <div className="space-y-4 mb-6">
          {PERMISSIONS_LIST.map((perm) => (
            <div key={perm.key} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50">
              <span className="font-medium text-gray-700">{perm.label}</span>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!permissions[perm.key]}
                  onChange={() => handleToggle(perm.key)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
              </label>
            </div>
          ))}
        </div>
      )}

      <button
        onClick={handleSave}
        className="bg-green-500 hover:bg-green-600 text-white px-6 py-2.5 rounded-lg font-semibold flex items-center gap-2 shadow-sm"
      >
        <FloppyDisk size={20} /> Save Changes
      </button>
    </div>
  );
}
